import { PropTypes } from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ScrollingContainer } from '@ge/components/scrolling-container';
import { useManualAdjustmentContext } from '@ge/feat-analyze/context/manual-adjustment-context';

import { EventDetailDialog } from './event-detail-dialog';
import SitePanelChartHeader from './site-panel-chart-header';
import SitePanelChartRow from './site-panel-chart-row';
import { ChartContainer } from './site-panel-chart-style';

const StyledScrollingContainer = styled(ScrollingContainer)`
  display: flex;
  position: relative;
  flex: 1;
  max-height: 320px;
  width: 100%;
  margin-bottom: 20px;
`;

export const SitePanelChart = ({ startOfDay, data, site, assetEventsKind }) => {
  const anchorEl = useRef(null);
  const chartRef = useRef([]);
  const location = useLocation();
  const firstTimeAssetSelectedRef = useRef(null);

  const forceReflow = (element) => {
    element.offsetHeight; // Access the element’s offsetHeight to force reflow
  };

  useEffect(() => {
    if (location?.state?.assetId && data?.length > 0 && !firstTimeAssetSelectedRef.current) {
      const tempIndex = data.findIndex((item) => item.asset?.id === location?.state?.assetId);

      if (tempIndex !== -1 && chartRef.current?.[tempIndex]) {
        forceReflow(chartRef.current[tempIndex]);
        setTimeout(() => {
          chartRef.current[tempIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 1500);
      }
      firstTimeAssetSelectedRef.current = true;
    }
  }, [data, data?.length, location?.state?.assetId]);

  const { selectedEvent, setSelectedEvent } = useManualAdjustmentContext();

  return (
    <ChartContainer
      ref={anchorEl}
      isEventSelected={selectedEvent && selectedEvent.site.id === site.id}
    >
      <SitePanelChartHeader data={data} />
      <StyledScrollingContainer relative>
        {data.map((item, i) => (
          <SitePanelChartRow
            ref={(el) => {
              if (el) chartRef.current[i] = el;
            }}
            key={item.asset?.id}
            data={item}
            site={site}
            startOfDay={startOfDay}
            allAssets={data}
          />
        ))}
      </StyledScrollingContainer>
      {selectedEvent && !selectedEvent.bulk && !selectedEvent.bulkEdit && (
        <EventDetailDialog
          isOpen={Boolean(selectedEvent)}
          onClose={() => setSelectedEvent(null)}
          site={site}
          event={selectedEvent?.event ?? null}
          anchorEl={anchorEl}
          portalId={`#eventDetailDialog-${site?.id}`}
          assetEventsKind={assetEventsKind}
          data={data}
        />
      )}
      <div
        id={`eventDetailDialog-${site?.id}`}
        className={`event-detail ${!selectedEvent ? 'hide' : ''}`}
      ></div>
    </ChartContainer>
  );
};

SitePanelChart.propTypes = {
  isLoading: PropTypes.bool,
  site: PropTypes.instanceOf(Object).isRequired,
  startOfDay: PropTypes.number.isRequired,
  data: PropTypes.array,
  assetEventsKind: PropTypes.string,
};

SitePanelChart.defaultProps = {
  isLoading: false,
  data: [],
};
